import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { Brain, HeartHandshake, Briefcase, Sparkles, ArrowRight, Lock } from "lucide-react";

const SECTION_META = {
  relationship: { icon: HeartHandshake, color: "#F59E0B", group: "Connection Lab",
    tagline: "How you connect, communicate love, and set boundaries." },
  work_style: { icon: Briefcase, color: "#38BDF8", group: "Work IQ",
    tagline: "How you decide, collaborate, and lead." },
};

export default function Plus() {
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [active, setActive] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/premium/modules");
        setModules(data.modules); setActive(data.access.active); setSessions(data.sessions || []);
      } catch (e) { if (e?.response?.status === 401) nav("/auth"); }
    })();
  }, [nav]);

  const start = async (id) => {
    try {
      const { data } = await api.post(`/premium/modules/${id}/start`);
      nav(`/plus/session/${data.session_id}`);
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("DualQ Plus required"); nav("/billing"); }
      else toast.error("Could not start module");
    }
  };

  return (
    <div><Nav />
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">DualQ Plus</div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-2" data-testid="plus-title">The premium hub</h1>
        <p className="text-slate-400 max-w-xl text-sm sm:text-base">
          Deeper explorations released as modules. Each one takes a few minutes and feeds back into your profile.
          {!active && <span className="block mt-2 text-amber-300">You're on the free tier — modules below are locked.</span>}
        </p>

        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          {modules.map(m => {
            const meta = SECTION_META[m.id] || { icon: Sparkles, color: "#A855F7", group: "Module", tagline: "" };
            const Icon = meta.icon;
            return (
              <div key={m.id} className="rounded-2xl bg-[#131E36] border border-slate-800/70 hover:border-[#E0A96D]/40 p-5" data-testid={`plus-module-${m.id}`}>
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{color: meta.color}}>
                  <Icon className="w-4 h-4" /> {meta.group}
                </div>
                <div className="font-serif text-xl mt-1">{m.title}</div>
                <p className="text-slate-400 text-sm mt-1">{m.description}</p>
                <div className="text-[11px] font-mono text-slate-500 mt-2">~{m.estimated_minutes} min</div>
                <button onClick={()=>start(m.id)}
                  className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${active ? "bg-[#E0A96D] text-[#070C18] hover:bg-[#F0C28B]" : "bg-slate-800 text-slate-400"}`}
                  data-testid={`start-module-${m.id}`}>
                  {active ? <>Start <ArrowRight className="w-3.5 h-3.5" /></> : <><Lock className="w-3.5 h-3.5" /> Unlock with DualQ Plus</>}
                </button>
              </div>
            );
          })}
        </div>

        {sessions.length > 0 && (
          <>
            <h2 className="mt-10 mb-3 font-serif text-2xl">Your Plus history</h2>
            <div className="space-y-2" data-testid="plus-sessions">
              {sessions.map(s => (
                <button key={s.id} onClick={()=>nav(`/plus/session/${s.id}/results`)}
                  className="w-full text-left rounded-xl bg-[#131E36] border border-slate-800/70 hover:border-[#E0A96D]/40 px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-serif">{(SECTION_META[s.module_id]?.group || s.module_id)}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{s.status} · {new Date(s.started_at).toLocaleString()}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
